import React from 'react';
import ReactBody from 'react-body';
import { StickyContainer } from 'react-sticky';
import { Element } from 'react-scroll';
import ActiveSection from '../../scripts/active-section';
import NavUnitTesting from '../header/NavUnitTesting';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Images
import ApiUnitTestOne from '../../images/knowledge/api-unit-test-one.png';
import ApiUnitTestTwo from '../../images/knowledge/api-unit-test-two.png';
import DebugOne from '../../images/knowledge/debug-one.png';
import DebugTwo from '../../images/knowledge/debug-two.png';
import DebugThree from '../../images/knowledge/debug-three.png';
import SolutionOne from '../../images/knowledge/solution-one.png';
import SolutionTwo from '../../images/knowledge/solution-two.png';
import SolutionThree from '../../images/knowledge/solution-three.png';
import UnitTestFix from '../../images/knowledge/unit-test-fix.png';

const UnitTesting = () => {
  var menuRef = React.createRef();
  
const ngTest = 
`ng test`;
  
const basicUnitTestOne = 
`import { Component } from '@angular/core' ;

(©Component ({
  selector: 'app-counter',
  template: '<button (click)="increment()">+</button> <span>{{count}}</span>',
})
export class CounterComponent { 
  count = 0;
  increment() { 
    this.count++;
  }
}`;
  
const basicUnitTestTwo = 
`import { ComponentFixture, TestBed } from '@angular/core/testing'; 
import { CounterComponent } from './counter.component'; // ✅ fixed import path

describe('CounterComponent', () => {
  let component: CounterComponent;
  let fixture: ComponentFixture<CounterComponent>;

  // Before each test, we configure the testing module and create the component
  beforeEach(() => {
    TestBed.configureTestingModule({
      declarations: [CounterComponent], // Declare the component to be tested 
    }).compileComponents(); // Compile component templates and CSS

    fixture = TestBed.createComponent(CounterComponent);
    component = fixture.componentInstance; // Get the component instance
  });

  it('should create the component', () => { 
    expect(component).toBeTruthy();
    // "toBeTruthy()" checks if the component instance is defined (not null or false)
  });

  it('should increment count', () => {
    component.increment(); // Call the increment function
    expect(component.count).toBe(1); // ✅ fixed typo from "i" to 1
    // "toBe(1)" checks if count has been updated to 1
  });
});`;

const testingServicesOne = 
`export class AuthService {
  isLoggedIn(): boolean {
    return !!localStorage.getItem('token'); // Returns true if token exists
  }
}`;

const testingServicesTwo = 
`import { AuthService } from './auth.service';

describe('AuthService', () => { 
  let service: AuthService;

  beforeEach(() => {
    service = new AuthService(); // Create a new instance of AuthService 
  });

  it('should return false if no token is present', () => {
    spyOn(localStorage, 'getItem').and.returnValue(null);
    // Mock localStorage.getItem to return null (no token)

    expect(service.isLoggedIn()).toBeFalse();
    // "toBeFalse()" asserts that the function returns false
  });

  it('should return true if token exists', () => {
    spyOn(localStorage, 'getItem').and.returnValue('mockToken');
    // Mock localStorage.getItem to return a token

    expect(service.isLoggedIn()).toBeTrue();
    // "toBeTrue()" asserts that the function returns true
  });
});`;

const testingDomInterations = 
`it('should update count on button click', () => {
  const button = fixture.nativeElement.querySelector('button');
  // Select the button element

  button.click(); // Simulate a button click
  fixture.detectChanges(); // Refresh the UI after the event

  const span = fixture.nativeElement.querySelector('span');
  // Select the span element

  expect(span.textContent).toContain('1') ;
  // Check if the span displays '1' after the button is clicked
});`;

const handlingAsync =
`import { fakeAsync, tick } from '(@angular/core/testing';

it('should wait for async function', fakeAsync(() => { 
  let value = false;

  setTimeout(() => {
    value = true;
  }, 1000); // Simulate a 1-second delay 

  tick(1000); // Simulate passage of 1 second
  expect(value).toBeTrue(); // After tick, value should be true
}));`;

const reproduceIssue =
`it('should filter items correctly and be case-insensitive', () => { 
  const items = ['Apple', 'Banana', 'Cherry']; 
  const result = service.filterItems(items, 'apple'); 
  expect(result).toEqual(['Apple']);
});`;

const fixIssueOne =
`this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((term) => { 
  this.filteredltems = this.searchService.filterItems(this.items, term);
});`;

const fixIssueTwo =
`it('should update search results after debounce delay', fakeAsync(() => { 
  component.searchControl.setValue('Ban'); 
  tick(300); // Simulate 300ms delay
  expect(component.filtereditems).toEqual(['Banana']);
}));`;

const apiService =
`import { Injectable } from '@angular/core'; 
import { HttpClient } from '@angular/common/http'; 
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private apiUrl = 'https://api.example.com/search'; // Mock API endpoint constructor(private http: HttpClient) {}
  // Function: searchltems
  // Purpose: Makes an API request to fetch fiLtered search results 
  searchItems(query: string): Observable<string[]> {
  return this.http.get<string[]>('\${this.apiUrl}?q=\${query}');
  }
}`;

const apiCalls =
`import { TestBed } from '@angular/core/testing';
import { HttpClientTestingModule, HttpTestingController } from '@angular/common/http/testing';
import { SearchService } from './search.service';

describe('SearchService', () => {
  let service: SearchService;
  let httpMock: HttpTestingController;

  beforeEach(() => {
    TestBed.configureTestingModule({
      imports: [HttpClientTestingModule],
      providers: [SearchService],
    });

    service = TestBed.inject(SearchService);
    httpMock = TestBed.inject(HttpTestingController);
  });

  afterEach(() => {
    httpMock.verify();
  });

  it('should fetch search results from API', () => {
    const mockResponse = ['Apple', 'Banana', 'Cherry'];

    service.searchItems('fruit').subscribe((data) => {
      expect(data).toEqual(mockResponse);
    });

    const req = httpMock.expectOne('https://api.example.com/search?q=fruit');
    expect(req.request.method).toBe('GET');
    req.flush(mockResponse);
  });

  it('should return an empty array if API returns no results', () => {
    service.searchItems('unknown').subscribe((data) => {
      expect(data.length).toBe(0);
    });

    const req = httpMock.expectOne('https://api.example.com/search?q=unknown');
    req.flush([]);
  });

  it('should handle API errors', () => {
    service.searchItems('error').subscribe(
      () => fail('Expected an error, but got results'),
      (error) => {
        expect(error.status).toBe(500);
      }
    );

    const req = httpMock.expectOne('https://api.example.com/search?q=error');
    req.flush('Something went wrong', { status: 500, statusText: 'Server Error' });
  });
});`;


  return (
    <section>
      <ReactBody className="knowledge-page" />
      <NavUnitTesting ref={menuRef} />
      <section className='right-section flex-group flex-align-center'>
      <StickyContainer>
        <Element className='element'>
          <section id="section-1" className='content-section flex-group flex-center'>
            <ActiveSection index="1" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Unit Testing in Angular (TDD Approach)</h2>
                <h3>What</h3>
                <p>
                  Unit testing is the process of testing small, isolated parts of an 
                  application to ensure they work as expected. Angular supports unit 
                  testing with Jasmine (test framework) and Karma (test runner).
                </p>
                <h3>Why</h3>
                <ul className="text-align-left margin-top-small">
                  <li>Ensures Code Quality – Catches bugs early.</li>
                  <li>Encourages Modular Design – Code is structured in a testable way.</li>
                  <li>Supports Refactoring – Allows developers to modify code without breaking functionality.</li>
                  <li>Improves Maintainability – Tests act as documentation for the future.</li>
                </ul>
                <h3>How</h3>
                <p>
                  <strong>(ng test)</strong> runs all unit tests in real-time. Whereas <strong>(ng test --coverage)</strong> runs 
                  tests & generates a code coverage report.
                </p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {ngTest}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-2" className='content-section flex-group flex-center'>
            <ActiveSection index="2" refToMenu={menuRef}>
              <section className='script text-align-left flex-group flex-center flex-column'>
                <h2>Writing a Basic Unit Test (Component Example)</h2>
                <h3>Example: Counter Component</h3>
                <p>
                  We have a simple CounterComponent that increments a number. <strong><br/>
                  it() is a function used to define a test case.</strong>
                </p>
                <h3>Component & Test spec</h3>
                <div className="flex-group flex-start">
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {basicUnitTestOne}
                </SyntaxHighlighter>
                  <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {basicUnitTestTwo}
                </SyntaxHighlighter>
                </div>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-3" className='content-section flex-group flex-center'>
            <ActiveSection index="3" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Testing Services (Mocking Dependencies with Jasmine Spies)</h2>
                <p>When testing services, we mock dependencies to avoid real API calls.</p>
                <h3>AuthService & Test File (auth.service.spec.ts)</h3>
                <div className="flex-group flex-start">
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {testingServicesOne}
                </SyntaxHighlighter>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {testingServicesTwo}
                </SyntaxHighlighter>
                </div>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-4" className='content-section flex-group flex-center'>
            <ActiveSection index="4" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Testing DOM Interactions (DebugElement & Querying Elements)</h2>
                <p>When testing components, we often need to verify UI changes.</p>
                <h3>Example: Checking Button Click Behavior</h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {testingDomInterations}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-5" className='content-section flex-group flex-center'>
            <ActiveSection index="5" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Step 5: Handling Async Code (Testing HTTP Calls & Timers)</h2>
                <p>For async operations like HTTP requests, we use FakeAsync & Tick.</p>
                <h3>Example: Simulating API Delay</h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {handlingAsync}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-6" className='content-section flex-group flex-center'>
            <ActiveSection index="6" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Final Thoughts</h2>
                <h3>Next Steps & Final Thoughts</h3>
                <ul className="text-align-left margin-top-med">
                  <li>Mocking HTTP Requests – Using HttpTestingController to test API calls.</li>
                  <li>Testing Directives & Pipes – Ensuring custom directives and pipes work correctly.</li>
                  <li>Improving Code Coverage – Running <strong>ng test --code-coverage</strong> to check test completeness.</li>
                  <li>Write small, focused test cases.</li>
                  <li>Use spies for mocking services.</li>
                  <li>Utilize FakeAsync for async operations.</li>
                  <li>Keep tests independent and isolated.</li>
                </ul>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-7" className='content-section flex-group flex-center'>
            <ActiveSection index="7" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Describe how solved an issue with a User Search?</h2>
                <p>
                  In one of my projects, we had a search feature where users could filter a 
                  list dynamically. A user reported that the search wasn’t working as 
                  expected—sometimes it wouldn’t return the correct results or wouldn’t 
                  update properly as they typed. I used unit testing to identify and solve 
                  the issue. Here’s how I approached it.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-8" className='content-section flex-group flex-center'>
            <ActiveSection index="8" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Step 1: Identify the problem</h2>
                <p>
                  First, I analyzed the possible causes of the issue. I suspected two main problems:<br/>
                  1️⃣ Case Sensitivity: Searching "apple" didn't match "Apple".<br/>
                  2️⃣ Debounce Timing Issue: The search results updated inconsistently due to rapid user input.<br/>
                  To verify this, I wrote unit tests for our search logic.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-9" className='content-section flex-group flex-center'>
            <ActiveSection index="9" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Step 2: Writing Unit Tests to Reproduce the Issue</h2>
                <p>
                  I started with the search service, which was responsible for 
                  filtering results. Here’s an example of a (test case function) I wrote:
                </p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {reproduceIssue}
                </SyntaxHighlighter>
                <p>
                  🛠️ This test confirmed the issue—the search was case-sensitive. 
                  I fixed it by converting everything to lowercase.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-10" className='content-section flex-group flex-center'>
            <ActiveSection index="10" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Step 3: Fixing the Issue & Testing Again</h2>
                <p>
                  Next, I addressed the debounce timing issue. Users typed quickly, 
                  causing unnecessary filtering calls. I modified the component 
                  to include debounce timing:
                </p>
                <h3>Adding debounceTime(300) was the fix here</h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fixIssueOne}
                </SyntaxHighlighter>
                <span className="margin-top-med">
                  Then, I wrote unit tests to ensure the debounce delay was working correctly:
                </span>
                <h3>This test uses fakeAsync() & tick() to simulate the delay. </h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fixIssueTwo}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-11" className='content-section flex-group flex-center'>
            <ActiveSection index="11" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Step 4: Confirming the Fix with Unit Tests</h2>
                <p>
                  After implementing these fixes, my unit tests confirmed that:
                </p>
                <ul className="text-align-left margin-top-med">
                  <li>The search was now case-insensitive.</li>
                  <li>Search results updated correctly after a debounce delay.</li>
                  <li>The bug was fully resolved and wouldn't break again.</li>
                </ul>
                <p>
                  This not only fixed the issue but also improved performance by 
                  preventing unnecessary filtering calls. The team was happy, 
                  and the users got a smoother search experience.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-12" className='content-section flex-group flex-center'>
            <ActiveSection index="12" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Final Takeaway (Why This Matters in TDD)</h2>
                <p>
                  This experience reinforced the power of unit testing for me. By writing tests first, I was able to:
                </p>
                <ul className="text-align-left margin-top-med">
                  <li>Identify and reproduce the issue quickly.</li>
                  <li>Implement a reliable, maintainable fix.</li>
                  <li>Prevent regressions, ensuring future changes wouldn’t break search functionality.</li>
                </ul>
                <p>
                  This was a great example of how TDD helps build reliable, user-friendly applications.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-13" className='content-section flex-group flex-center'>
            <ActiveSection index="13" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>API Service (search.service.ts</h2>
                <p>
                  This service makes an HTTP request to fetch search results:
                </p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {apiService}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-14" className='content-section flex-group flex-center'>
            <ActiveSection index="14" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Writing Unit Tests for API Calls (search.service.spec.ts)</h2>
                  <p>Use HttpClientTestingModule to provide a mock HTTP client.</p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {apiCalls}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-15" className='content-section flex-group flex-center'>
            <ActiveSection index="15" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Explanation of the Code</h2>
                <h3>Import (HttpTestingController)</h3>
                <ul className="text-align-left margin-top-med">
                  <li>Intercepts HTTP requests so we can control responses.</li>
                  <li>Prevents actual API calls from being made.</li>
                </ul>
                <h3>Simulate the backend response with req.flush(data)</h3>
                <ul className="text-align-left margin-top-med">
                  <li>Sends a mock response to the test request.</li>
                  <li>Simulates API responses without making a real request.</li>
                </ul>
                <h3>Error Handling Test</h3>
                <ul className="text-align-left margin-top-med">
                  <li>Simulates a 500 server error to check if our service can handle it properly.</li>
                </ul>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-16" className='content-section flex-group flex-center'>
            <ActiveSection index="16" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Final Answer for the Knowledge</h2>
                <p>
                  To test API calls in Angular, I use HttpClientTestingModule and 
                  HttpTestingController. This allows me to mock HTTP requests and 
                  control responses without making actual API calls. I write test 
                  cases to verify successful responses, handle empty results, and 
                  check for error handling. This approach ensures our application 
                  correctly interacts with APIs while keeping tests fast and reliable.
                </p>
              </section>
            </ActiveSection>
          </section>
        </Element>
      </StickyContainer>
      </section>
    </section>
  );
};

export default UnitTesting;